// ShiftCard — compact, calm shift row used inside "Mijn Rooster".
// One card = one shift, mobile-first, minimal visual noise.
import { Repeat, Coffee, StickyNote } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

function computeHours(start, end, pauseMin = 0) {
  const [sh, sm] = (start || "0:0").split(":").map(Number);
  const [eh, em] = (end || "0:0").split(":").map(Number);
  let grossMin = eh * 60 + em - (sh * 60 + sm);
  if (grossMin < 0) grossMin += 24 * 60;
  return Math.max(0, (grossMin - pauseMin) / 60);
}

/**
 * @param {object} props
 * @param {object} props.shift          — shift record from API
 * @param {string} props.accentColor    — location color (used as left rail + role dot)
 * @param {(s:object)=>void} props.onSwap
 */
export default function ShiftCard({ shift, accentColor = "#2563EB", onSwap }) {
  const { t } = useI18n();
  const pauseMin = typeof shift.pause_minutes === "number" ? shift.pause_minutes : 0;
  const hasPause = pauseMin > 0;
  const netHours = computeHours(shift.start_time, shift.end_time, pauseMin);
  const netLabel = (t("payroll_net") || "netto").toLowerCase();

  return (
    <div
      className="group relative flex flex-col gap-3 px-4 py-3.5 transition-colors hover:bg-[hsl(var(--surface-hover))]"
      data-testid={`my-shift-${shift._id}`}
    >
      {/* Colored accent rail — location identity */}
      <span
        aria-hidden
        className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full"
        style={{ background: accentColor }}
      />

      {/* Row 1 — Time (headline) + Swap action */}
      <div className="flex items-start justify-between gap-3 pl-3">
        <div className="min-w-0">
          <div className="font-display text-[20px] leading-none font-bold text-[hsl(var(--text-strong))] tracking-tight tabular-nums">
            {shift.start_time}
            <span className="mx-1.5 text-[hsl(var(--text-soft))] font-normal">–</span>
            {shift.end_time}
          </div>
          {shift.role_label && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[12.5px] text-[hsl(var(--text-muted))] font-medium">
              <span
                aria-hidden
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: accentColor }}
              />
              <span className="truncate">{shift.role_label}</span>
            </div>
          )}
        </div>

        <button
          onClick={() => onSwap?.(shift)}
          className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-soft))] transition-colors"
          title={t("swap_shift")}
          aria-label={t("swap_shift")}
          data-testid={`request-swap-${shift._id}`}
        >
          <Repeat size={15} strokeWidth={2} />
        </button>
      </div>

      {/* Row 2 — Meta (net hours · pause) inline, calm */}
      <div className="pl-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[hsl(var(--text-muted))] tabular-nums">
        <span className="inline-flex items-baseline gap-1">
          <span className="font-semibold text-[hsl(var(--text))]">{netHours.toFixed(1)}u</span>
          <span className="text-[11px]">{netLabel}</span>
        </span>
        <span aria-hidden className="text-[hsl(var(--text-soft))]">·</span>
        <span
          className={`inline-flex items-center gap-1 ${hasPause ? "text-[hsl(38_92%_45%)]" : "text-[hsl(var(--text-soft))]"}`}
          data-testid={`my-shift-pause-${shift._id}`}
        >
          <Coffee size={12} strokeWidth={2} />
          {hasPause
            ? `${pauseMin} ${t("pause_minutes_short") || "min pauze"}`
            : t("no_pause") || "Geen pauze"}
        </span>
      </div>

      {/* Row 3 — Notes (only if present) — quiet callout */}
      {shift.notes && (
        <div
          className="ml-3 rounded-lg bg-[hsl(var(--primary-soft))] px-3 py-2 text-[12.5px] text-[hsl(var(--text-strong))]"
          data-testid={`my-shift-notes-${shift._id}`}
        >
          <div className="mb-0.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--primary))]">
            <StickyNote size={11} strokeWidth={2.4} />
            {t("shift_notes")}
          </div>
          <div className="whitespace-pre-wrap leading-relaxed">{shift.notes}</div>
        </div>
      )}
    </div>
  );
}
